import { Global } from '@emotion/react';
import { customErrorMap } from '@features/ui-form/services/customErrorMap';
import GlobalStyle from '@features/ui-layout/styles/globalStyle';
import theme from '@features/ui-layout/styles/theme';
import { LayoutLoggedin } from '@features/ui-layout/templates/LayoutLoggedin';
import { CssVarsProvider } from '@mui/joy';
import i18nConfig from '@root/next-i18next.config';
import { useRealtimeSession } from '@root/src/hooks/useRealtimeSession';
import { ReactQueryConfig } from '@root/src/services/api/reactQueryConfig';
import { NextPageWithLayout } from '@root/src/utils/types/NextPageWithLayout';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { z } from 'zod';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

z.setErrorMap(customErrorMap);

const MyApp = ({ Component, ...pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  const { t } = useTranslation();

  // This helps React notice when the same layout is reused across pages
  // see: https://nextjs.org/docs/basic-features/layouts
  const getLayout = Component.getLayout ?? (page => <LayoutLoggedin>{page}</LayoutLoggedin>);

  const [queryClient] = useState(() => ReactQueryConfig(router, t));

  // Keep the React Query cache in sync across tabs/windows of the same browser
  // via BroadcastChannel: a mutation (e.g. member edit) in one tab is reflected
  // in the others without a manual reload. Client-only — BroadcastChannel does
  // not exist during SSR. Affects the whole app's cache, not a single page.
  useEffect(() => {
    broadcastQueryClient({ queryClient, broadcastChannel: 'spacetalk-rq' });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Default browser tab title. A page that needs its own renders its own
          <Head><title> — the closest one wins. Everything else inherits this,
          which is also what the unread badge prefixes itself to. */}
      <Head>
        <title>{t('app.title')}</title>
      </Head>
      <Global styles={GlobalStyle} />
      <CssVarsProvider theme={theme}>
        {/* Handle post-SSO return redirect */}
        <SSOReturnHandler />
        {/* Closes the socket on logout; stays mounted on /login, unlike the logged-in pages */}
        <RealtimeSession />
        {getLayout(<Component {...pageProps} />)}
        <ToastContainer />
      </CssVarsProvider>
    </QueryClientProvider>
  );
};

export default appWithTranslation(MyApp, i18nConfig);

const RealtimeSession = () => {
  useRealtimeSession();
  return null;
};

const SSOReturnHandler = () => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const ssoReturn = urlParams.get('sso_return');
    if (ssoReturn === 'true') {
      // Clean the sso_return query parameter and stay on current page
      const currentPath = window.location.pathname;
      window.history.replaceState({}, document.title, currentPath);

      // Only redirect to pre-login URL if we're on a login-related page
      if (currentPath === '/login' || currentPath === '/sso-wisekey/login') {
        try {
          const preLoginUrl = sessionStorage.getItem('preLoginUrl') || '/';
          sessionStorage.removeItem('preLoginUrl');
          window.location.href = preLoginUrl;
        } catch (error) {
          // Fallback if sessionStorage is unavailable (e.g., privacy mode)
          console.warn('SessionStorage unavailable, redirecting to home:', error);
          window.location.href = '/';
        }
      }
      // Otherwise, stay on current page (user is already authenticated via SSO)
    }
  }, []);
  return null;
};
